import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import compact from 'lodash/compact';
import {
  EligibilityRequestDTO,
  PayerEntryDTO,
  CheckEligibilityResponseDTO,
  InsuranceSettingsDTO,
  PatientSummaryDTO,
  UpsertInsuranceRequestDTO,
  InsuranceDTO,
  InsuranceAttachmentDTO,
  AssociateInsuranceCardRequestDTO,
  SyncInsuranceAttachmentsRequestDTO,
  UploadAndAssociateInsuranceCardRequestDTO,
  InsuranceSlotOptionDTO,
  MultiMediaResponse,
} from 'dtos';
import {
  InsuranceSearchType,
} from 'dtos/insuranceSearchType';
import {
  InsuranceSlot,
} from 'dtos/insuranceSlot';
import {
  isEmptyString,
  isNilOrZero,
  sanitizeFileName,
} from '@populateui/utils';
import {
  deleteApiPatientDeleteInsuranceInsuranceId,
  getApiCHGetPayerEntryById,
  getApiCHGetFavoriteInsurances,
  getApiCHSearchInsurance,
  getApiPatientGetInsuranceAttachmentsInsuranceId,
  getApiPatientGetUnassociatedInsuranceCardsPatientId,
  getApiPatientGetPatientInsurancesByIdId,
  getApiPatientGetPatientSummaryPatientId,
  getApiSettingsGetInsuranceSettings,
  postApiCHCheckEligibility,
  postApiPatientAddInsurance,
  postApiPatientSyncInsuranceAttachments,
  postApiPatientUploadAndAssociateInsuranceCardWithInsurance,
  postApiPatientAssociateInsuranceCardWithInsurance,
  putApiPatientUpdateInsurance,
  deleteApiPatientDisassociateInsuranceCardInsuranceAttachmentId,
} from 'endpoints';
import {
  useCallback,
  useMemo,
} from 'react';
import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import isNil from 'lodash/isNil';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';

type UploadInsuranceAttachmentParams = {
  insuranceId: number;
  patientId: number;
  files: FileLocalUrl[];
};

type UploadInsuranceAttachment = UseMutationResult<
  InsuranceAttachmentDTO,
  Error,
  UploadAndAssociateInsuranceCardRequestDTO
> & {
  uploadAllInsuranceAttachments: (
    params: UploadInsuranceAttachmentParams,
  ) => Promise<InsuranceAttachmentDTO[]>;
};

export function useUpsertInsurances(): UseMutationResult<
  InsuranceDTO, Error, UpsertInsuranceRequestDTO> {
  return useMutation<InsuranceDTO, Error, UpsertInsuranceRequestDTO>({
    mutationFn: (payload: UpsertInsuranceRequestDTO) => {
      const requestFn = isNilOrZero(payload.insurance?.insuranceId)
        ? postApiPatientAddInsurance
        : putApiPatientUpdateInsurance;
      return requestFn(payload);
    },
  });
}

export function useDeleteInsurance(): UseMutationResult<
  void, Error, number> {
  return useMutation<void, Error, number>({
    mutationFn: (
      insuranceId: number,
    ) => deleteApiPatientDeleteInsuranceInsuranceId(insuranceId),
  });
}

export function useGetPatientInsurances(
  patientId: number = 0,
): UseQueryResult<InsuranceDTO[], Error> {
  return useQuery<InsuranceDTO[], Error>({
    queryKey: ['patient-insurances', patientId],
    queryFn: ({ signal }) => getApiPatientGetPatientInsurancesByIdId(
      patientId,
      { activeOnly: false },
      { signal },
    ),
    enabled: patientId > 0,
  });
}

export function useGetPatientSummary(
  patientId: number = 0,
): UseQueryResult<PatientSummaryDTO, Error> {
  return useQuery<PatientSummaryDTO, Error>({
    queryKey: ['patient-summary', patientId],
    queryFn: () => getApiPatientGetPatientSummaryPatientId(patientId),
    enabled: patientId > 0,
  });
}

export function useGetInsuranceAttachments(
  insuranceId: number = 0,
): UseQueryResult<InsuranceAttachmentDTO[], Error> {
  return useQuery<InsuranceAttachmentDTO[], Error>({
    queryKey: ['insurance-attachments', insuranceId],
    queryFn: () => getApiPatientGetInsuranceAttachmentsInsuranceId(insuranceId),
    enabled: insuranceId > 0,
  });
}

export function useGetUnassociatedInsuranceCards(
  patientId: number = 0,
): UseQueryResult<MultiMediaResponse[], Error> {
  return useQuery<MultiMediaResponse[], Error>({
    queryKey: ['unassociated-insurance-cards', patientId],
    queryFn: async () => {
      const result = await getApiPatientGetUnassociatedInsuranceCardsPatientId(patientId);
      return result?.list ?? [];
    },
    enabled: patientId > 0,
  });
}

export function useUploadInsuranceAttachments(): UploadInsuranceAttachment {
  const {
    mutateAsync: uploadInsuranceAttachment,
    ...mutation
  } = useMutation<
    InsuranceAttachmentDTO,
    Error,
    UploadAndAssociateInsuranceCardRequestDTO
  >({
    mutationFn: (
      data: UploadAndAssociateInsuranceCardRequestDTO,
    ) => postApiPatientUploadAndAssociateInsuranceCardWithInsurance(data),
  });

  const uploadAllInsuranceAttachments = ({
    insuranceId,
    patientId,
    files,
  }: UploadInsuranceAttachmentParams) => Promise.all(
    files.map(({ dataUrl, file }) => uploadInsuranceAttachment({
      insuranceId,
      media: {
        patientId,
        fileName: sanitizeFileName(file?.name ?? ''),
        dataUrl: dataUrl ?? '',
        multimediaType: MultimediaType.InsuranceCard,
      },
    })),
  );

  return {
    ...mutation,
    mutateAsync: uploadInsuranceAttachment,
    uploadAllInsuranceAttachments,
  };
}

export function useAssociateInsuranceCard(): UseMutationResult<
  InsuranceAttachmentDTO,
  Error,
  AssociateInsuranceCardRequestDTO
  > {
  return useMutation<
    InsuranceAttachmentDTO,
    Error,
    AssociateInsuranceCardRequestDTO
  >({
    mutationFn: (
      associateInsuranceCardRequestDTO: AssociateInsuranceCardRequestDTO,
    ) => postApiPatientAssociateInsuranceCardWithInsurance(associateInsuranceCardRequestDTO),
  });
}

export function useDisassociateInsuranceCard(): UseMutationResult<
  void,
  Error,
  number
  > {
  return useMutation<void, Error, number>({
    mutationFn: (
      insuranceAttachmentId: number,
    ) => deleteApiPatientDisassociateInsuranceCardInsuranceAttachmentId(
      insuranceAttachmentId,
    ),
  });
}

export function useSyncInsuranceAttachments(): UseMutationResult<
  InsuranceAttachmentDTO[],
  Error,
  SyncInsuranceAttachmentsRequestDTO
  > {
  return useMutation<
    InsuranceAttachmentDTO[],
    Error,
    SyncInsuranceAttachmentsRequestDTO
  >({
    mutationFn: (
      data: SyncInsuranceAttachmentsRequestDTO,
    ) => postApiPatientSyncInsuranceAttachments(data),
  });
}

export function useCheckEligibility(): UseMutationResult<
  CheckEligibilityResponseDTO, Error, EligibilityRequestDTO> {
  return useMutation<CheckEligibilityResponseDTO, Error, EligibilityRequestDTO>({
    mutationFn: async (options) => {
      const response = await postApiCHCheckEligibility(options);
      return response as CheckEligibilityResponseDTO;
    },
  });
}

export function useInsuranceSearch(
  insuranceType?: string,
  search: string = '',
  searchType: InsuranceSearchType = 'All',
  enabled: boolean = true,
): UseQueryResult<PayerEntryDTO[], Error> {
  return useQuery<PayerEntryDTO[], Error>({
    queryKey: ['insurance-lookup', search],
    queryFn: async () => {
      const insuranceList = await getApiCHSearchInsurance({ search, insuranceType, searchType });
      return compact(insuranceList);
    },
    enabled: enabled && !isEmptyString(search),
  });
}

export function useGetPayerEntryById(
  payerEntryId?: number | null,
  enabled: boolean = true,
): UseQueryResult<PayerEntryDTO | null, Error> {
  return useQuery<PayerEntryDTO | null, Error>({
    queryKey: ['payer-entry', payerEntryId],
    queryFn: async () => {
      if (isNil(payerEntryId)) {
        return null;
      }

      return getApiCHGetPayerEntryById({ payerEntryId });
    },
    enabled: enabled && !isNil(payerEntryId),
  });
}

export function useGetFavoriteInsurances(): UseQueryResult<
  PayerEntryDTO[], Error> {
  return useQuery<PayerEntryDTO[], Error>({
    queryKey: ['insurance-favorites'],
    queryFn: async () => {
      const insuranceList = await getApiCHGetFavoriteInsurances();
      return compact(insuranceList);
    },
  });
}

export function useInsuranceMetaData() {
  const { data } = usePrefetchedData<InsuranceSettingsDTO>({
    key: ['insurance-meta'],
    fetchFn: getApiSettingsGetInsuranceSettings,
  });

  const {
    mspTypes = [],
    relationshipTypes,
    insuranceSlotOptions,
    insuranceWorkflowResolutionTypeOptions,
  } = (data ?? {}) as InsuranceSettingsDTO & {
    insuranceSlotOptions?: InsuranceSlotOptionDTO[];
  };

  const relationshipTypesOptions = useMemo(
    () => compact(map(relationshipTypes, 'name')),
    [relationshipTypes],
  );

  const relationshipTypesByName = useMemo(
    () => keyBy(relationshipTypes ?? [], 'name'),
    [relationshipTypes],
  );

  const getInsuranceSlotLabel = useCallback(
    (slot: InsuranceSlot) =>
      insuranceSlotOptions
        ?.find((option) => option.slot === slot)
        ?.displayName ?? slot,
    [insuranceSlotOptions],
  );

  const restrictedSlotSet = new Set(insuranceSlotOptions
    ?.filter((option) => option.isRestrictedToUniqueActiveInsurance)
    ?.map((option) => option.slot)
    ?? []);

  const insuranceSlotValues = insuranceSlotOptions
    ?.filter((option) => !isNil(option.slot))?.map((option) => option.slot!) ?? [];

  return {
    relationshipTypes: relationshipTypesOptions,
    relationshipTypesByName,
    mspTypes,
    insuranceSlotOptions,
    restrictedSlotSet,
    insuranceSlotValues,
    getInsuranceSlotLabel,
    insuranceWorkflowResolutionTypeOptions: insuranceWorkflowResolutionTypeOptions ?? [],
  };
}
